import { budgetApi, getAccessToken } from '@/lib/api';
import { budgetDb, deviceUnlockDb } from '@/lib/db';
import { queueOfflineChange, shouldUseLocalData } from '@/lib/offlineQueue';
import type {
  Budget,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  CopyBudgetRequest,
  BudgetStatus,
  BudgetSummary,
  DashboardStats,
} from '@/types';

async function resolveOwnerId(explicit?: string): Promise<string> {
  if (explicit) return explicit;
  const unlock = await deviceUnlockDb.get();
  if (unlock?.userId) return unlock.userId;
  const token = getAccessToken();
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]!));
      if (payload.sub) return payload.sub as string;
    } catch {
      /* ignore */
    }
  }
  throw new Error('Not signed in');
}

/**
 * Budget domain service.
 * Online: backend API. Offline / localUnlocked: Dexie + sync queue.
 */
export const BudgetService = {
  /**
   * Fetch all root budget summaries for the dashboard (paginated API, all pages).
   */
  async getDashboardSummaries(): Promise<BudgetSummary[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const ownerId = await resolveOwnerId();
      const budgets = await this.getAll(ownerId);
      return budgets.filter((b) => !b.parentId).map((b) => this.toSummary(b));
    }

    const pageSize = 100;
    let page = 0;
    const all: BudgetSummary[] = [];

    while (true) {
      const response = await budgetApi.getSummariesPage({ page, size: pageSize });
      all.push(...response.content);
      if (page >= response.totalPages - 1) {
        break;
      }
      page += 1;
    }

    return all;
  },

  async getActiveSummaries(userId: string): Promise<BudgetSummary[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const budgets = await this.getActive(userId);
      return budgets.filter((b) => !b.parentId).map((b) => this.toSummary(b));
    }
    return budgetApi.getActiveSummaries();
  },

  async getSummaries(userId: string): Promise<BudgetSummary[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const budgets = await this.getAll(userId);
      return budgets.filter((b) => !b.parentId).map((b) => this.toSummary(b));
    }
    return budgetApi.getSummaries();
  },

  async getAll(userId: string): Promise<Budget[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const ownerId = userId || (await resolveOwnerId());
      const budgets = await budgetDb.getAll(ownerId);
      return this.applyHierarchy(budgets);
    }
    const budgets = await budgetApi.getAll();
    return this.applyHierarchy(budgets);
  },

  async getActive(userId: string): Promise<Budget[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const ownerId = userId || (await resolveOwnerId());
      const budgets = await budgetDb.getActive(ownerId);
      return this.applyHierarchy(budgets);
    }
    const budgets = await budgetApi.getActive();
    return this.applyHierarchy(budgets);
  },

  async getById(id: string): Promise<Budget | null> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const budget = await budgetDb.getById(id);
      return budget ?? null;
    }
    const budget = await budgetApi.getById(id);
    return budget ?? null;
  },

  async create(request: CreateBudgetRequest): Promise<Budget> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const ownerId = await resolveOwnerId();
      const now = new Date().toISOString();
      const budget: Budget = {
        id: crypto.randomUUID(),
        name: request.name,
        type: request.type,
        parentId: request.parentId ?? null,
        currency: request.currency,
        amount: request.amount,
        startDate: request.startDate,
        endDate: request.endDate,
        rolloverEnabled: request.rolloverEnabled ?? false,
        rolloverAmount: request.rolloverAmount ?? null,
        copiedFromId: request.copyFromId ?? null,
        ownerId,
        shared: false,
        owned: true,
        canEdit: true,
        version: 0,
        lastModified: now,
        spent: 0,
        remaining: request.amount,
        percentSpent: 0,
        autoClassificationEnabled: request.autoClassificationEnabled,
      };
      await budgetDb.save(budget);
      await queueOfflineChange({
        entityType: 'BUDGET',
        entityId: budget.id,
        changeType: 'CREATE',
        data: JSON.stringify(budget),
        version: budget.version,
      });
      return budget;
    }
    return budgetApi.create(request);
  },

  async update(id: string, request: UpdateBudgetRequest): Promise<Budget> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const existing = await budgetDb.getById(id);
      if (!existing) throw new Error('Budget not found offline');
      const updated: Budget = {
        ...existing,
        name: request.name,
        type: request.type,
        parentId: request.parentId ?? existing.parentId,
        currency: request.currency,
        amount: request.amount,
        startDate: request.startDate,
        endDate: request.endDate,
        rolloverEnabled: request.rolloverEnabled ?? existing.rolloverEnabled,
        rolloverAmount: request.rolloverAmount ?? existing.rolloverAmount,
        autoClassificationEnabled:
          request.autoClassificationEnabled ?? existing.autoClassificationEnabled,
        version: request.version ?? existing.version + 1,
        lastModified: new Date().toISOString(),
      };
      await budgetDb.save(updated);
      await queueOfflineChange({
        entityType: 'BUDGET',
        entityId: id,
        changeType: 'UPDATE',
        data: JSON.stringify(updated),
        version: updated.version,
      });
      return updated;
    }
    return budgetApi.update(id, request);
  },

  async delete(id: string, confirmation?: boolean): Promise<void> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const existing = await budgetDb.getById(id);
      await budgetDb.delete(id);
      await queueOfflineChange({
        entityType: 'BUDGET',
        entityId: id,
        changeType: 'DELETE',
        data: JSON.stringify(existing ?? { id }),
        version: existing?.version ?? 0,
      });
      return;
    }
    await budgetApi.delete(id, confirmation);
  },

  async copyToNewPeriod(request: CopyBudgetRequest): Promise<Budget> {
    if (!navigator.onLine || shouldUseLocalData(!!getAccessToken())) {
      throw new Error('Copying budgets requires an internet connection');
    }
    return budgetApi.copy(request);
  },

  calculateStatus(budget: Budget): BudgetStatus {
    const percentSpent = budget.percentSpent ?? 0;

    if (percentSpent > 100) {
      return 'OVER_BUDGET';
    }

    if (percentSpent >= 90) {
      return 'NEAR_LIMIT';
    }

    if (budget.type === 'OBLIGATION') {
      return this.calculateObligationStatus(budget);
    }

    return 'ON_TRACK';
  },

  calculateObligationStatus(budget: Budget): BudgetStatus {
    const today = new Date();
    const start = new Date(budget.startDate);
    const end = new Date(budget.endDate);

    if (today > end) {
      return (budget.percentSpent ?? 0) < 100 ? 'AHEAD' : 'ON_TRACK';
    }

    const totalDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    const elapsedDays = (today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

    if (totalDays <= 0) return 'ON_TRACK';

    const expectedPercent = (elapsedDays / totalDays) * 100;
    const actualPercent = budget.percentSpent ?? 0;

    return actualPercent < expectedPercent ? 'AHEAD' : 'ON_TRACK';
  },

  toSummary(budget: Budget): BudgetSummary {
    return {
      id: budget.id,
      name: budget.name,
      type: budget.type,
      currency: budget.currency,
      amount: budget.amount,
      spent: budget.spent ?? 0,
      remaining: budget.remaining ?? budget.amount,
      percentSpent: budget.percentSpent ?? 0,
      status: budget.status ?? this.calculateStatus(budget),
      startDate: budget.startDate,
      endDate: budget.endDate,
      hasChildren: (budget.children?.length ?? 0) > 0,
      childCount: budget.children?.length ?? 0,
    };
  },

  async getDashboardStats(userId: string): Promise<DashboardStats> {
    const budgets = await this.getActive(userId);

    let totalBudgeted = 0;
    let totalSpent = 0;
    let overBudgetCount = 0;
    const upcomingObligations: Budget[] = [];
    const nearLimitBudgets: Budget[] = [];

    for (const budget of budgets) {
      totalBudgeted += budget.amount;
      totalSpent += budget.spent ?? 0;

      const status = budget.status ?? this.calculateStatus(budget);

      if (status === 'OVER_BUDGET') {
        overBudgetCount++;
      }

      if (status === 'NEAR_LIMIT') {
        nearLimitBudgets.push(budget);
      }

      if (budget.type === 'OBLIGATION' && (budget.percentSpent ?? 0) < 100) {
        upcomingObligations.push(budget);
      }
    }

    return {
      totalBudgeted,
      totalSpent,
      totalRemaining: totalBudgeted - totalSpent,
      activeBudgets: budgets.length,
      upcomingObligations: upcomingObligations.slice(0, 5),
      nearLimitBudgets: nearLimitBudgets.slice(0, 5),
      overBudgetCount,
    };
  },

  validate(data: Partial<CreateBudgetRequest>): string[] {
    const errors: string[] = [];

    if (!data.name?.trim()) {
      errors.push('Name is required');
    }

    if (!data.type) {
      errors.push('Budget type is required');
    }

    if (!data.currency || data.currency.length !== 3) {
      errors.push('Valid currency code is required');
    }

    if (data.amount === undefined || data.amount < 0) {
      errors.push('Amount must be non-negative');
    }

    if (!data.startDate) {
      errors.push('Start date is required');
    }

    if (!data.endDate) {
      errors.push('End date is required');
    }

    if (data.startDate && data.endDate && data.startDate > data.endDate) {
      errors.push('End date must be after start date');
    }

    return errors;
  },

  applyHierarchy(budgets: Budget[]): Budget[] {
    if (!budgets?.length) return budgets ?? [];

    const flatten = (items: Budget[]): Budget[] => {
      const result: Budget[] = [];
      const stack = [...items];
      while (stack.length) {
        const node = stack.pop()!;
        result.push(node);
        (node.children ?? []).forEach((child) => stack.push(child));
      }
      return result;
    };

    const flatBudgets = flatten(budgets);

    const budgetMap = new Map<string, Budget>();
    flatBudgets.forEach((budget) => {
      budgetMap.set(budget.id, { ...budget, children: [] });
    });

    const roots: Budget[] = [];
    budgetMap.forEach((budget) => {
      if (budget.parentId && budgetMap.has(budget.parentId)) {
        const parent = budgetMap.get(budget.parentId)!;
        parent.children = parent.children ?? [];
        parent.children.push(budget);
      } else {
        roots.push(budget);
      }
    });

    const rollup = (node: Budget): { transactionCount: number } => {
      let aggregatedTransactions = node.transactionCount ?? 0;

      for (const child of node.children ?? []) {
        const childTotals = rollup(child);
        aggregatedTransactions += childTotals.transactionCount;
      }

      node.transactionCount = aggregatedTransactions;
      node.status = this.calculateStatus(node);

      return { transactionCount: aggregatedTransactions };
    };

    roots.forEach((root) => rollup(root));

    return Array.from(budgetMap.values());
  },
};
